/**
 * Shared checklist types for API contracts between frontend and backend
 *
 * Checklist is the unified model for actionable work items that can be assigned to:
 * - Shifts (shift-specific tasks)
 * - Employments (person-specific tasks)
 * - Locations (location-wide tasks)
 */
import { z } from "zod";
import { nameSchema, uuidSchema, IdParamSchema, } from "./validation-common.js";
// ===== ENUMS =====
export const ChecklistSourceTypeSchema = z.enum([
    "MANUAL",
    "TEMPLATE",
    "ALERT_DEESCALATION",
]);
export const ChecklistPrioritySchema = z.enum(["NORMAL", "HIGH", "URGENT"]);
// ===== CHECKLIST ITEM STRUCTURE =====
/**
 * ChecklistItem - stored in Checklist.items JSON array
 * Contains completion tracking only (no task reference)
 */
export const ChecklistItemSchema = z.object({
    id: uuidSchema,
    description: z.string().trim().min(1).max(500),
    completed: z.boolean(),
    completedAt: z.iso.datetime().nullish(), // ISO date string - nullish() accepts null from JSON
    completedBy: uuidSchema.nullish(), // User ID - nullish() accepts null from JSON
});
// ===== ASSIGNMENT DISCRIMINATED UNION =====
const ShiftAssignmentSchema = z.object({
    assignmentType: z.literal("SHIFT"),
    shiftId: uuidSchema,
    employmentId: uuidSchema.optional(), // Can combine if shift.employeeId matches
});
const EmploymentAssignmentSchema = z.object({
    assignmentType: z.literal("EMPLOYMENT"),
    employmentId: uuidSchema,
});
const LocationAssignmentSchema = z.object({
    assignmentType: z.literal("LOCATION"),
    locationId: uuidSchema,
});
export const ChecklistAssignmentSchema = z.discriminatedUnion("assignmentType", [
    ShiftAssignmentSchema,
    EmploymentAssignmentSchema,
    LocationAssignmentSchema,
]);
// ===== INPUT SCHEMAS =====
/**
 * Schema for creating a checklist
 */
export const CreateChecklistInputSchema = z.object({
    name: nameSchema,
    items: z
        .array(z.object({
        description: z.string().trim().min(1).max(500),
    }))
        .min(1),
    assignment: ChecklistAssignmentSchema,
    companyId: uuidSchema,
    requiresPhoto: z.boolean().optional(),
    priority: ChecklistPrioritySchema.optional(),
    dueDate: z.iso.datetime().optional(),
    sourceTemplateId: uuidSchema.optional(),
});
/**
 * Schema for creating a checklist from an alert
 */
export const CreateChecklistFromAlertInputSchema = z.object({
    name: nameSchema,
    items: z
        .array(z.object({
        description: z.string().trim().min(1).max(500),
    }))
        .min(1),
    companyId: uuidSchema,
    requiresPhoto: z.boolean().optional(),
    priority: ChecklistPrioritySchema.optional(),
});
/**
 * Schema for updating a checklist
 */
export const UpdateChecklistInputSchema = z.object({
    name: nameSchema.optional(),
    priority: ChecklistPrioritySchema.optional(),
    dueDate: z.iso.datetime().nullable().optional(),
    requiresPhoto: z.boolean().optional(),
    checklistCompleted: z.boolean().optional(), // Force-complete entire checklist
});
/**
 * Schema for completing a checklist item
 */
export const CompleteChecklistItemInputSchema = z.object({
    itemId: uuidSchema,
});
/**
 * Schema for uncompleting a checklist item
 */
export const UncompleteChecklistItemInputSchema = z.object({
    itemId: uuidSchema,
});
/**
 * Schema for adding a photo to a checklist
 */
export const AddChecklistPhotoInputSchema = z.object({
    fileUploadId: uuidSchema,
});
/**
 * Schema for reassigning a checklist
 */
export const ReassignChecklistInputSchema = z.object({
    assignment: ChecklistAssignmentSchema,
});
// ===== ROUTE PARAMETER SCHEMAS =====
export const ChecklistIdParamSchema = IdParamSchema;
export const ItemIdParamSchema = z.object({
    itemId: uuidSchema,
});
// ===== QUERY PARAMETER SCHEMAS =====
export const GetChecklistsQuerySchema = z.object({
    companyId: uuidSchema,
    shiftId: uuidSchema.optional(),
    employmentId: uuidSchema.optional(),
    locationId: uuidSchema.optional(),
    completed: z
        .enum(["true", "false"])
        .optional()
        .transform((val) => val === "true" ? true : val === "false" ? false : undefined),
    priority: ChecklistPrioritySchema.optional(),
    sourceType: ChecklistSourceTypeSchema.optional(),
});
// ===== RESPONSE SCHEMAS =====
export const ChecklistPhotoResponseSchema = z.object({
    id: z.uuid(),
    fileUploadId: z.uuid(),
    fileUpload: z.object({
        id: z.uuid(),
        s3Key: z.string(),
        fileName: z.string(),
        mimeType: z.string(),
    }),
    uploadedById: z.uuid().optional(),
    uploadedBy: z.object({
        id: z.uuid(),
        firstName: z.string(),
        lastName: z.string(),
    }).optional(),
    uploadedAt: z.iso.datetime(),
});
export const ChecklistResponseSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    items: z.array(ChecklistItemSchema),
    checklistCompleted: z.boolean(),
    // Assignment
    shiftId: z.uuid().optional(),
    employmentId: z.uuid().optional(),
    locationId: z.uuid().optional(),
    // Shift relation
    shift: z.object({
        id: z.uuid(),
        startTime: z.iso.datetime(),
        endTime: z.iso.datetime(),
        employeeId: z.uuid().optional(),
        jobRoleFamily: z.object({
            name: z.string(),
        }).optional(),
    }).optional(),
    // Photo requirements
    requiresPhoto: z.boolean(),
    photos: z.array(ChecklistPhotoResponseSchema),
    // Source tracking
    sourceType: ChecklistSourceTypeSchema,
    sourceTemplateId: z.uuid().optional(),
    alertInstanceId: z.uuid().optional(),
    // Metadata
    priority: ChecklistPrioritySchema,
    dueDate: z.iso.datetime().optional(),
    companyId: z.uuid(),
    createdBy: z.uuid().optional(),
    createdByUser: z.object({
        id: z.uuid(),
        firstName: z.string(),
        lastName: z.string(),
    }).optional(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
